import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { omit } from 'lodash-es';
import { combineLatestAlwaysEmit } from '../../../helpers/observables/combine-latest-helper';
import { cacheValueWhileSubscribed, isNotNullOrUndefined } from '../../../helpers/typed-reactive-filters.helper';
import { BaseNestedFieldDefinition, DottedKey } from '../../field-definitions/entities/field-definition.entity';
import { FieldDefinitionHelper } from '../../field-definitions/helpers/field-definition.helper';
import { ShFieldDefinitionService } from '../../field-definitions/services/field-definition.service';
import { OrganisationId } from '../../organisations/entities/organisation.entity';
import { ProfileDataService } from '../data-services/profile.data-service';
import { ExtendedBaseProfileField, ExtendedProfileField } from '../entities/extended-profile-field.entity';
import { ExtendedProfile } from '../entities/extended-profile.entity';
import { BaseProfileField, ProfileField } from '../entities/profile-field.entity';
import { Profile, ProfileCategory } from '../entities/profile.entity';

@Injectable({ providedIn: 'root' })
export class ExtendedProfileService {
    readonly updateCache$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

    private readonly fieldDefinitionService: ShFieldDefinitionService = inject(ShFieldDefinitionService);

    constructor(private readonly profileService: ProfileDataService) {}

    public getExtended(profileId: string, organisationId?: OrganisationId): Observable<ExtendedProfile | undefined>;
    public getExtended(profileIds: string[], organisationId?: OrganisationId): Observable<ExtendedProfile[]>;
    public getExtended(
        profileId: string | string[],
        organisationId?: OrganisationId
    ): Observable<(ExtendedProfile | undefined) | ExtendedProfile[]> {
        if (Array.isArray(profileId)) {
            return this.getMultipleExtended(profileId, organisationId);
        } else {
            return this.getMultipleExtended([profileId], organisationId).pipe(
                map(result => (result.length === 1 ? result[0] : undefined))
            );
        }
    }

    public getExtendedProfileOfCategoryById(
        profileId: string,
        category: ProfileCategory,
        organisationId?: OrganisationId
    ): Observable<ExtendedProfile | undefined> {
        switch (category) {
            case ProfileCategory.Base:
            case ProfileCategory.Classification:
            case ProfileCategory.GenericList:
            case ProfileCategory.Facet:
                return this.getExtended(profileId);
            default:
                return this.extendGenericCategory(profileId, category, organisationId);
        }
    }

    public extendGenericCategory(
        profileId: string,
        category: ProfileCategory,
        organisationId?: OrganisationId
    ): Observable<ExtendedProfile | undefined> {
        const profile$: Observable<Profile | undefined> = this.profileService.getSingle(
            profileId,
            category,
            organisationId
        );

        const definitions$: Observable<BaseNestedFieldDefinition[]> = profile$.pipe(
            switchMap(profile =>
                profile
                    ? this.fieldDefinitionService.getByProfileIds(
                          [profileId],
                          profile?.Fields.map(field => field.DottedKey)
                      )
                    : of([])
            )
        );

        return combineLatest([profile$, definitions$]).pipe(
            map(([profile, definitions]) => (profile ? this.extendProfile(profile, definitions) : undefined))
        );
    }

    public getAdvancedSearchProfileFields(
        organisationId: OrganisationId,
        tag?: string
    ): Observable<ExtendedBaseProfileField[]> {
        const profiles$: Observable<Profile[]> = this.profileService
            .getAdvancedSearchProfiles(organisationId, tag)
            .pipe(cacheValueWhileSubscribed());
        const profileFields$: Observable<ProfileField[]> = profiles$.pipe(
            map(profiles => profiles.flatMap(profile => profile.Fields)),
            cacheValueWhileSubscribed(1)
        );
        const fielddefinitions$: Observable<BaseNestedFieldDefinition[]> = profiles$.pipe(
            switchMap(profiles => {
                const profileIds: string[] = profiles.map(profile => profile.Id);
                const dottedKeys: DottedKey[] = profiles.flatMap(profile =>
                    profile.Fields.map(field => field.DottedKey)
                );

                return this.fieldDefinitionService.getByProfileIds(profileIds, dottedKeys);
            }),
            cacheValueWhileSubscribed(1)
        );

        return combineLatest([profileFields$, fielddefinitions$]).pipe(
            map(([fields, fieldDefinitions]) => {
                if (fields.length === 0) {
                    console.error(`ProfileDataService:: No advanced search profile fields found.`);
                    return [];
                }

                return fields.map(field => this.extendProfileField(field, fieldDefinitions));
            }),
            isNotNullOrUndefined()
        );
    }

    // #endregion

    // #region Getters
    public getProfileFieldByFieldDefinitionId(
        id: number,
        organisationId: OrganisationId
    ): Observable<ExtendedBaseProfileField | undefined> {
        return this.getAdvancedSearchProfileFields(organisationId).pipe(
            map(fields => fields.find(field => field.FieldDefinitionId === id))
        );
    }

    public getProfileFieldByDottedKey(
        dottedKey: string,
        organisationId: OrganisationId
    ): Observable<ExtendedBaseProfileField | undefined> {
        return this.getAdvancedSearchProfileFields(organisationId).pipe(
            map(fields => fields.find(field => field.FieldDefinition.DottedKey === dottedKey))
        );
    }

    private getMultipleExtended(profileIds: string[], organisationId?: OrganisationId): Observable<ExtendedProfile[]> {
        return this.updateCache$.pipe(
            switchMap(() => this.profileService.get(profileIds, ProfileCategory.Classification)),
            map(profiles => profiles.filter(profile => profileIds.includes(profile.Id))),
            switchMap(profiles =>
                this.fieldDefinitionService
                    .getByProfileIds(
                        profiles.map(profile => profile.Id), // Use profiles since profileIds can contain deleted profiles
                        profiles.flatMap(profile => profile.Fields.map((field: ProfileField) => field.DottedKey))
                    )
                    .pipe(map(definitions => [profiles, definitions] as [Profile[], BaseNestedFieldDefinition[]]))
            ),
            switchMap(([profiles, definitions]) =>
                combineLatestAlwaysEmit(profiles.map(profile => this.getSingleExtended(profile, definitions)))
            ),
            take(1),
            map(profiles => profiles.filter((profile): profile is ExtendedProfile => typeof profile !== 'undefined'))
        );
    }

    private getSingleExtended(
        profile: Profile,
        definitions?: BaseNestedFieldDefinition[]
    ): Observable<ExtendedProfile | undefined> {
        let definitions$: Observable<BaseNestedFieldDefinition[]>;
        if (!definitions) {
            definitions$ = profile
                ? this.fieldDefinitionService.getByProfileIds(
                      [profile.Id],
                      profile?.Fields.map(field => field.DottedKey)
                  )
                : of([]);
        } else {
            definitions$ = of(definitions);
        }

        return definitions$.pipe(map(definitions => (profile ? this.extendProfile(profile, definitions) : undefined)));
    }
    // #endregion

    // #region Save
    public createExtendedProfile(extendedProfile: ExtendedProfile): Observable<Profile> {
        const profile: Profile = ExtendedProfileService.stripExtendedProfile(extendedProfile);

        return this.profileService.createProfile(profile);
    }

    public saveExtendedProfile(extendedProfile: ExtendedProfile): Observable<boolean> {
        const profile: Profile = ExtendedProfileService.stripExtendedProfile(extendedProfile);

        return this.profileService.saveProfile(profile);
    }
    // #endregion

    // #region Create
    /**
     * Created a profile field.
     *
     * @param {string} profileId - The ID of the profile
     * @param {Partial<BaseProfileField>} field - The data for the field to be created
     * @return {Observable<boolean>} Observable that emits a boolean indicating the success of the deletion
     */
    public createProfileField(profileId: string, field: Partial<BaseProfileField>): Observable<boolean> {
        return this.profileService.createProfileField(profileId, field);
    }
    // #endregion

    // #region Delete

    /**
     * Deletes a profile field.
     *
     * @param {string} profileId - The ID of the profile
     * @param {string} fieldDottedKey - The ID of the field to be deleted
     * @return {Observable<boolean>} Observable that emits a boolean indicating the success of the deletion
     */
    public deleteProfileField(profileId: string, fieldDottedKey: string): Observable<boolean> {
        return this.profileService.deleteProfileField(profileId, fieldDottedKey);
    }

    // #endregion

    // #region Extend Utils
    private extendProfile(profile: Profile, fieldDefinitions: BaseNestedFieldDefinition[]): ExtendedProfile {
        if (!profile.Fields || profile.Fields.length === 0) {
            return profile as ExtendedProfile;
        }

        const extendedProfileFields: ExtendedBaseProfileField[] = profile.Fields.map(field =>
            this.extendProfileField(field, fieldDefinitions)
        );
        return {
            ...profile,
            Fields: extendedProfileFields
        };
    }

    private extendProfileField(
        profileField: ProfileField,
        fieldDefinitions: BaseNestedFieldDefinition[]
    ): ExtendedBaseProfileField {
        const rootKey: DottedKey | undefined = FieldDefinitionHelper.getDottedKeyRoot(profileField.DottedKey);
        const rootFieldDefinition: BaseNestedFieldDefinition | undefined = fieldDefinitions.find(
            fieldDefinition => fieldDefinition.DottedKey === rootKey
        );

        if (!rootFieldDefinition) {
            console.error(`Root fieldDefinition ${profileField.DottedKey} was not found`);
        }

        const profileFieldDefinition: BaseNestedFieldDefinition | undefined = rootFieldDefinition
            ? FieldDefinitionHelper.getNestedByDottedKey(rootFieldDefinition, profileField.DottedKey)
            : undefined;

        if (!profileFieldDefinition) {
            console.error(`FieldDefinition ${profileField.DottedKey} was not found`);
        }

        return {
            ...profileField,
            FieldDefinition: profileFieldDefinition
        } as ExtendedBaseProfileField;
    }

    public static stripExtendedProfile(extendedProfile: ExtendedProfile): Profile {
        const profile: Profile = this.cleanProfileObject(extendedProfile);

        return {
            ...profile,
            Fields: extendedProfile.Fields.map(field => this.stripExtendedProfileField(field))
        };
    }

    private static stripExtendedProfileField(extendedField: ExtendedProfileField): ProfileField {
        return omit(this.cleanProfileObject(extendedField), ['FieldDefinition']) as ProfileField;
    }

    // Remove null, undefined or empty properties from Profile or ProfileFields objects
    private static cleanProfileObject(obj: any): any {
        for (const propName in obj) {
            if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
                delete obj[propName];
            }

            // Special handling of translations
            // Remove translations with empty values
            // Delete property if no translations are left
            if (propName === 'Descriptions' || propName === 'Labels' || propName === 'Names') {
                const translations = obj[propName];
                const cleanedTranslations = translations.filter(
                    (translation: any) =>
                        translation.Value !== null && translation.Value !== undefined && translation.Value !== ''
                );
                if (cleanedTranslations.length === 0) {
                    delete obj[propName];
                } else {
                    obj[propName] = cleanedTranslations;
                }
            }
        }
        return obj;
    }

    // #endregion
}
